// Combat-sim battle state and lifecycle. See docs/05-combat-sim §5.5 for the
// canonical pipeline and §5.5.1 for end conditions.
import { Rng } from '../rng.js';
/** Default time limits (in ticks at 30 Hz). See docs/05-combat-sim §5.5.1. */
export const NORMAL_BATTLE_TIME_LIMIT_TICKS = 30 * 90; // 90 seconds
export const BOSS_BATTLE_TIME_LIMIT_TICKS = 30 * 180; // 180 seconds
/** Default config for a normal (non-boss) battle. */
export function defaultConfig() {
    return {
        isBoss: false,
        timeoutHpThreshold: 0.3,
    };
}
/** Resolve the effective tick limit from config. */
export function getTimeLimit(config) {
    return (config.timeLimitTicks ??
        (config.isBoss ? BOSS_BATTLE_TIME_LIMIT_TICKS : NORMAL_BATTLE_TIME_LIMIT_TICKS));
}
/** Initialise a fresh battle state from a seed and the participant lists. */
export function initBattle(seed, allies, enemies, config = {}) {
    return {
        tick: 0,
        rng: Rng.fromSeed(seed),
        entities: [...allies, ...enemies],
        events: [],
        outcome: null,
        recentAttackers: new Map(),
        config: { ...defaultConfig(), ...config },
        placeables: [],
        summons: [],
        scheduledEffects: [],
        critHookFiring: false,
        elementBranchDepth: 0,
        counterStances: [],
        storeAndReleasePools: [],
        sharedAbsorbPools: [],
        phaseCyclingAuras: [],
        anchoredHealHooks: [],
        damageConversions: [],
        periodicSkillState: new Map(),
        lastTakenDamageTick: new Map(),
        summonByEntity: new Map(),
        reactiveHpFlags: new Map(),
        summonAttackedThisTick: new Set(),
        firedReactiveThisTick: new Map(),
        reactiveChainDepth: 0,
        damageReactOverrides: new Map(),
    };
}
/** Restore a battle state from a snapshot (for replay or PvP verification). */
export function restoreBattle(snapshot) {
    return {
        tick: snapshot.tick,
        rng: Rng.fromState(snapshot.rngState),
        entities: snapshot.entities,
        events: snapshot.events,
        outcome: snapshot.outcome,
        recentAttackers: new Map(snapshot.recentAttackers),
        config: snapshot.config,
        placeables: snapshot.placeables ?? [],
        summons: snapshot.summons ?? [],
        scheduledEffects: snapshot.scheduledEffects ?? [],
        critHookFiring: false,
        elementBranchDepth: 0,
        counterStances: snapshot.counterStances ?? [],
        storeAndReleasePools: snapshot.storeAndReleasePools ?? [],
        sharedAbsorbPools: snapshot.sharedAbsorbPools ?? [],
        phaseCyclingAuras: snapshot.phaseCyclingAuras ?? [],
        anchoredHealHooks: snapshot.anchoredHealHooks ?? [],
        damageConversions: snapshot.damageConversions ?? [],
        // ADR 0014: 復元時は空状態から開始。replay / PvP verification では「battle 中の
        // periodic / reactive flag 状態」は snapshot の event log から再構築される
        // (該当 reducer 群が tick.ts step 経由で再計算)。
        periodicSkillState: new Map(),
        lastTakenDamageTick: new Map(),
        summonByEntity: new Map(),
        reactiveHpFlags: new Map(),
        summonAttackedThisTick: new Set(),
        firedReactiveThisTick: new Map(),
        reactiveChainDepth: 0,
        damageReactOverrides: new Map(snapshot.damageReactOverrides ?? []),
    };
}
//# sourceMappingURL=state.js.map